import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Card,
  CardBody,
  Button,
  Input,
  Spinner,
  Badge,
  Row,
  Col,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Eye,
  Pencil,
  Search,
  Plus,
  RefreshCw,
  Database,
  Star,
  Building2,
} from "lucide-react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import cachedData from "../../cache/contractors_select.json";
import { Contractor } from "../../models/Contractor.interface";
import { formatAddress } from "../../models/Address.interface";
import { useFirebaseDedup } from "../../hooks/useFirebaseDedup";

interface ContractorsTableProps {
  onSelectContractor?: (contractor: Contractor) => void;
}

const ContractorsTable: React.FC<ContractorsTableProps> = ({
  onSelectContractor,
}) => {
  // States for all contractors and filtered results
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [filteredContractors, setFilteredContractors] = useState<Contractor[]>(
    []
  );

  // State for search input and whether search has been performed
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // Other UI states
  const [loading, setLoading] = useState(true);
  const [useCachedData, setUseCachedData] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const navigate = useNavigate();
  const { dedupRequest } = useFirebaseDedup();
  const isFetchingRef = useRef(false);

  // Fetch cached data function
  const fetchCachedData = useCallback(async () => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setHasSearched(false);

      // Filter out entries without required fields
      const validContractors = cachedData.filter(
        (item) => item.businessName && item.contractorId
      ) as Contractor[];

      setContractors(validContractors);
      setFilteredContractors(validContractors);
      setLastUpdated("Cached Data");
    } catch (error) {
      console.error("Error loading cached contractor data:", error);
      setIsError(true);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Fetch live data function
  const fetchLiveData = useCallback(
    async (isFullSearch = false) => {
      if (isFetchingRef.current) return;

      try {
        isFetchingRef.current = true;
        setLoading(true);
        setIsError(false);

        if (!isFullSearch) {
          setHasSearched(false);
        }

        const firestore = firebase.firestore();

        // Use limit for initial load, but not for searches
        const queryFn = isFullSearch
          ? () => firestore.collection("contractors").get()
          : () => firestore.collection("contractors").limit(50).get();

        // Use dedupRequest to prevent duplicate requests
        const contractorsSnapshot = await dedupRequest(
          isFullSearch ? "contractors-full-list" : "contractors-list",
          queryFn,
          30 * 24 * 60 * 60 * 1000, // 30 days cache duration
          'indexedDB' // Use IndexedDB for persistent storage
        );

        const contractorsList = contractorsSnapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Contractor)
        );

        // Filter out entries without required fields
        const validContractors = contractorsList.filter(
          (item) => item.businessName && item.contractorId
        );

        setContractors(validContractors);

        // Only filter if a search has been performed
        if (isFullSearch && searchTerm) {
          performSearch(validContractors);
        } else {
          setFilteredContractors(validContractors);
        }

        setLastUpdated("Live Data");
      } catch (error) {
        console.error("Error fetching live contractor data:", error);
        setIsError(true);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [dedupRequest]
  );

  // Initial data loading
  useEffect(() => {
    const fetchData = async () => {
      if (useCachedData) {
        await fetchCachedData();
      } else {
        await fetchLiveData(false);
      }
    };

    fetchData();
  }, [useCachedData, fetchCachedData, fetchLiveData]);

  // Search function with case-insensitive matching
  const performSearch = useCallback(
    (dataToSearch: Contractor[]) => {
      if (!searchTerm.trim()) {
        setFilteredContractors(dataToSearch);
        return;
      }

      const lowerCaseSearch = searchTerm.toLowerCase();
      const filtered = dataToSearch.filter((contractor) => {
        // Check business name (case insensitive)
        if (
          contractor.businessName &&
          contractor.businessName.toLowerCase().includes(lowerCaseSearch)
        ) {
          return true;
        }

        // Check email (case insensitive)
        if (
          contractor.email &&
          contractor.email.toLowerCase().includes(lowerCaseSearch)
        ) {
          return true;
        }

        // Check phone
        if (contractor.phone && contractor.phone.includes(searchTerm)) {
          return true;
        }

        // Check categories (case insensitive)
        if (
          contractor.categories &&
          contractor.categories.some((cat) =>
            cat.toLowerCase().includes(lowerCaseSearch)
          )
        ) {
          return true;
        }

        // Check address (case insensitive)
        if (
          contractor.address &&
          formatAddress(contractor.address)
            .toLowerCase()
            .includes(lowerCaseSearch)
        ) {
          return true;
        }

        // Check general info (case insensitive)
        if (
          contractor.generalInfo &&
          contractor.generalInfo.toLowerCase().includes(lowerCaseSearch)
        ) {
          return true;
        }

        // Check description (case insensitive)
        if (
          contractor.description &&
          contractor.description.toLowerCase().includes(lowerCaseSearch)
        ) {
          return true;
        }

        // Check search terms in location data
        if (contractor.searches && contractor.searches.length > 0) {
          for (const search of contractor.searches) {
            if (
              (search.city &&
                search.city.toLowerCase().includes(lowerCaseSearch)) ||
              (search.state &&
                search.state.toLowerCase().includes(lowerCaseSearch))
            ) {
              return true;
            }
          }
        }

        return false;
      });
      
      const limitedResults = filtered.slice(0, 50);
      setFilteredContractors(limitedResults);
    },
    [searchTerm]
  );

  // Handle search submission (on button click or Enter)
  const handleSearch = useCallback(() => {
    setHasSearched(true);
  
    if (useCachedData) {
      // When using cached data, search through existing contractors
      performSearch(contractors);
    } else {
      // When using live data, fetch all contractors first
      fetchLiveData(true);
    }
  }, [useCachedData, contractors, performSearch, fetchLiveData]);

  // Handle search input keypress - only trigger search on Enter
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Handle data source toggle
  const handleDataSourceToggle = (useCache: boolean) => {
    if (useCache !== useCachedData && !isFetchingRef.current) {
      setUseCachedData(useCache);
      // Reset search when toggling data source
      setSearchTerm("");
      setHasSearched(false);
    }
  };

  const handleAddContractor = () => {
    navigate("/contractor/add");
  };

  const handleViewProfile = (contractor: Contractor) => {
    if (onSelectContractor) {
      onSelectContractor(contractor);
    } else {
      navigate(`/contractor-profile/${contractor.contractorId}`);
    }
  };

  const handleEditContractor = (contractor: Contractor) => {
    navigate(`/contractor/edit/${contractor.contractorId}`);
  };

  if (isError) {
    return (
      <Card>
        <CardBody>
          <div className="text-center text-danger">
            Error loading contractors data. Please try again later.
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div>
      <Card className="mb-4 shadow-sm">
        <CardBody className="p-4">
          <Row className="align-items-center">
            <Col lg={4}>
              <div className="d-flex align-items-center">
                <Building2 size={24} className="text-primary me-2" />
                <div>
                  <h4 className="mb-1">Contractors Directory</h4>
                  {lastUpdated && (
                    <p className="text-muted mb-0">
                      Last updated: {lastUpdated}
                    </p>
                  )}
                </div>
              </div>
            </Col>
            <Col lg={8}>
              <div className="d-flex gap-3 justify-content-lg-end">
                <div className="flex-grow-1" style={{ maxWidth: "400px" }}>
                  <InputGroup>
                    <InputGroupText className="bg-light">
                      <Search size={18} />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Search by name, category, or location..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      disabled={loading}
                      className="border-start-0"
                    />
                    <Button
                      color="primary"
                      onClick={handleSearch}
                      disabled={loading}
                    >
                      Search
                    </Button>
                  </InputGroup>
                </div>
                <Button
                  color="light"
                  className="d-flex align-items-center"
                  onClick={() => handleDataSourceToggle(!useCachedData)}
                  disabled={loading}
                >
                  {useCachedData ? (
                    <Database size={18} className="me-2" />
                  ) : (
                    <RefreshCw size={18} className="me-2" />
                  )}
                  {useCachedData ? "Cached" : "Live"}
                </Button>
                <Button
                  color="primary"
                  className="d-flex align-items-center"
                  onClick={handleAddContractor}
                >
                  <Plus size={18} className="me-2" />
                  Add Contractor
                </Button>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="shadow-sm">
        <CardBody className="p-0">
          {loading ? (
            <div className="text-center p-5">
              <Spinner color="primary" />
              <p className="mt-2">Loading contractors...</p>
            </div>
          ) : filteredContractors.length === 0 ? (
            <div className="text-center p-5">
              <Building2 size={48} className="text-muted mb-3" />
              <h4>No Contractors Found</h4>
              <p className="text-muted">
                {hasSearched
                  ? "No contractors match your search criteria. Try adjusting your search terms."
                  : "No contractors available. Try changing your data source or adding new contractors."}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0" style={{ width: "30%" }}>
                      Business Information
                    </th>
                    <th className="border-0" style={{ width: "20%" }}>
                      Contact Details
                    </th>
                    <th className="border-0" style={{ width: "20%" }}>
                      Location
                    </th>
                    <th className="border-0" style={{ width: "20%" }}>
                      Categories
                    </th>
                    <th
                      className="border-0 text-center"
                      style={{ width: "10%" }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContractors.map((contractor) => (
                    <tr key={contractor.contractorId}>
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          {contractor.logo ? (
                            <img
                              src={contractor.logo}
                              alt={contractor.businessName}
                              className="rounded-circle object-fit-cover me-3"
                              style={{ width: "48px", height: "48px" }}
                            />
                          ) : (
                            <div
                              className="flex-shrink-0 me-3 rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                              style={{ width: "48px", height: "48px" }}
                            >
                              <span className="fs-5">
                                {contractor.businessName.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div>
                            <h6 className="mb-1">{contractor.businessName}</h6>
                            {contractor.rating && (
                              <div className="d-flex align-items-center">
                                <Star size={14} className="text-warning me-1" />
                                <span className="text-muted small">
                                  {contractor.rating.toFixed(1)}
                                  {contractor.reviewCount && (
                                    <span>
                                      {" "}
                                      ({contractor.reviewCount} reviews)
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        {contractor.phone && (
                          <div className="mb-1 d-flex align-items-center">
                            <Phone
                              size={14}
                              className="me-2 text-primary"
                              style={{ minWidth: "14px" }}
                            />
                            <span>{contractor.phone}</span>
                          </div>
                        )}
                        {contractor.email && (
                          <div className="d-flex align-items-center">
                            <Mail
                              size={14}
                              className="me-2 text-primary"
                              style={{ minWidth: "14px" }}
                            />
                            <span>{contractor.email}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="d-flex align-items-center">
                          <MapPin
                            size={14}
                            className="me-2 text-primary"
                            style={{ minWidth: "14px" }}
                          />
                          <span>
                            {contractor.address
                              ? formatAddress(contractor.address)
                              : "No address provided"}
                          </span>
                        </div>
                      </td>
                      <td className="py-3">
                        <div className="d-flex flex-wrap gap-1">
                          {contractor.categories
                            ?.slice(0, 3)
                            .map((category, index) => (
                              <Badge
                                key={index}
                                color="light"
                                className="text-primary"
                              >
                                {category}
                              </Badge>
                            ))}
                          {contractor.categories &&
                            contractor.categories.length > 3 && (
                              <Badge color="primary">
                                +{contractor.categories.length - 3}
                              </Badge>
                            )}
                        </div>
                      </td>
                      <td className="text-center py-3">
                        <Button
                          color="light"
                          size="sm"
                          className="me-2"
                          onClick={() => handleViewProfile(contractor)}
                        >
                          <Eye size={14} />
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          onClick={() => handleEditContractor(contractor)}
                        >
                          <Pencil size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          <div
            className="my-3 text-muted small"
            style={{ marginInline: "9.6px" }}
          >
            Showing {filteredContractors.length} of {contractors.length}{" "}
            contractors
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default ContractorsTable;
